/**
 * Custom toast hook using react-hot-toast
 *
 * Provides consistent toast notifications throughout the app with
 * pre-configured styling for success, error, warning, and info messages.
 */

import toast, { Toaster } from 'react-hot-toast'

export { Toaster }

interface ToastOptions {
  duration?: number
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'
}

/**
 * Default toast configuration
 */
const DEFAULT_OPTIONS: ToastOptions = {
  duration: 4000,
  position: 'top-right',
}

/**
 * Toast hook with pre-styled notification methods
 */
export function useToast() {
  const success = (message: string, options?: ToastOptions) => {
    return toast.success(message, {
      duration: options?.duration || DEFAULT_OPTIONS.duration,
      position: options?.position || DEFAULT_OPTIONS.position,
      style: {
        background: '#1f2937',
        color: '#ffffff',
        border: '1px solid #10b981',
      },
      iconTheme: {
        primary: '#10b981',
        secondary: '#1f2937',
      },
    })
  }

  const error = (message: string, options?: ToastOptions) => {
    return toast.error(message, {
      duration: options?.duration || DEFAULT_OPTIONS.duration,
      position: options?.position || DEFAULT_OPTIONS.position,
      style: {
        background: '#1f2937',
        color: '#ffffff',
        border: '1px solid #ef4444',
      },
      iconTheme: {
        primary: '#ef4444',
        secondary: '#1f2937',
      },
    })
  }

  const warning = (message: string, options?: ToastOptions) => {
    return toast(message, {
      duration: options?.duration || DEFAULT_OPTIONS.duration,
      position: options?.position || DEFAULT_OPTIONS.position,
      icon: '⚠️',
      style: {
        background: '#1f2937',
        color: '#ffffff',
        border: '1px solid #f59e0b',
      },
    })
  }

  const info = (message: string, options?: ToastOptions) => {
    return toast(message, {
      duration: options?.duration || DEFAULT_OPTIONS.duration,
      position: options?.position || DEFAULT_OPTIONS.position,
      icon: 'ℹ️',
      style: {
        background: '#1f2937',
        color: '#ffffff',
        border: '1px solid #3b82f6',
      },
    })
  }

  const loading = (message: string) => {
    return toast.loading(message, {
      style: {
        background: '#1f2937',
        color: '#ffffff',
        border: '1px solid #6b7280',
      },
    })
  }

  const dismiss = (toastId?: string) => {
    toast.dismiss(toastId)
  }

  return {
    success,
    error,
    warning,
    info,
    loading,
    dismiss,
  }
}

/**
 * Specific error message handlers
 */
export function useErrorToasts() {
  const toast = useToast()

  const websocketError = () => {
    toast.error('Lost connection to server. Attempting to reconnect...')
  }

  const websocketReconnected = () => {
    toast.success('Reconnected to server')
  }

  const websocketMaxReconnectFailed = () => {
    toast.error('Unable to reconnect to server. Please refresh the page to try again.', {
      duration: 8000,
    })
  }

  const spaceTrackRateLimit = (retryAfterSeconds: number = 60) => {
    return toast.warning(
      `Space-Track API rate limit reached. Please wait ${retryAfterSeconds} seconds before trying again.`,
      {
        duration: retryAfterSeconds * 1000,
      }
    )
  }

  const toolExecutionError = (toolName: string) => {
    toast.error(`Failed to execute ${toolName}. Please try again.`)
  }

  const genericError = (message?: string) => {
    toast.error(message || 'An unexpected error occurred. Please try again.')
  }

  return {
    websocketError,
    websocketReconnected,
    websocketMaxReconnectFailed,
    spaceTrackRateLimit,
    toolExecutionError,
    genericError,
  }
}
